'use client';

import {
    PLANNER_STATUS_LABELS,
    useActivePlannerReports,
} from '@/@studio-ai/hooks/useActivePlannerReports';
import type { ActivePlannerReportEntry } from '@/@studio-ai/hooks/useActivePlannerReports';
import { Button, Progress } from '@heroui/react';
import { Icon } from '@iconify/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

const STATUS_PERCENT: Record<string, number> = {
    NOT_FOUND: 5,
    PLANNED: 15,
    INSIGHTS_READY: 40,
    RUNNING: 65,
    ANALYSES_COMPLETE: 85,
    COMPLETE: 100,
    FAILED: 100,
};

function ReportToastEntry({
    entry,
    onDismiss,
    onDone,
}: {
    entry: ActivePlannerReportEntry;
    onDismiss: () => void;
    /** Stop tracking a finished run once its report is opened. */
    onDone: () => void;
}) {
    const router = useRouter();
    const status = entry.lastStatus ?? 'PLANNED';
    const isComplete = status === 'COMPLETE';
    const isFailed = status === 'FAILED';
    const isTerminal = isComplete || isFailed;
    const label = PLANNER_STATUS_LABELS[status] ?? 'Your report is being generated...';
    const percent = STATUS_PERCENT[status] ?? 5;

    const openReport = () => {
        if (!entry.lastReportId) return;
        if (entry.outputFormat === 'pptx') {
            router.push(`/dani/${entry.conversationId}/${entry.lastReportId}`);
        } else {
            router.push(`/dani/${entry.conversationId}`);
        }
        onDone();
    };

    // PPTX reports navigate automatically when complete — no "View" click needed.
    useEffect(() => {
        if (entry.outputFormat === 'pptx' && isComplete && entry.lastReportId) {
            openReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entry.outputFormat, isComplete, entry.lastReportId]);

    const brandLabel = entry.brandLabel ? `${entry.brandLabel} report` : 'Report in progress';

    return (
        <motion.div
            key={entry.runId}
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ duration: 0.18 }}
            role="status"
            aria-atomic="true"
            className="pointer-events-auto flex w-72 flex-col rounded-xl border border-default-200 bg-content1 px-4 py-3 shadow-lg"
        >
            {/* Header */}
            <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-1.5">
                    {isComplete && (
                        <Icon
                            icon="solar:check-circle-bold"
                            className="shrink-0 text-success"
                            width={16}
                            height={16}
                        />
                    )}
                    {isFailed && (
                        <Icon
                            icon="solar:danger-circle-bold"
                            className="shrink-0 text-danger"
                            width={16}
                            height={16}
                        />
                    )}
                    <span className="truncate text-sm font-medium">{brandLabel}</span>
                </div>
                <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    aria-label="Hide"
                    className="-mr-1 shrink-0"
                    onPress={onDismiss}
                >
                    <Icon icon="solar:close-circle-linear" width={16} height={16} />
                </Button>
            </div>

            {/* Progress bar */}
            <div className="mt-2.5 flex flex-col gap-1">
                <div className="flex items-center justify-between">
                    <span className="text-xs text-default-500">{label}</span>
                    <span className="ml-3 shrink-0 text-xs font-semibold tabular-nums text-foreground/70">
                        {percent}%
                    </span>
                </div>
                <Progress
                    aria-label={label}
                    value={percent}
                    size="sm"
                    radius="full"
                    color={isFailed ? 'danger' : isComplete ? 'success' : 'primary'}
                    classNames={{ track: 'bg-default-200/60' }}
                />
            </div>

            {/* A run can't be stopped from Studio yet, so the toast offers no Cancel:
                closing it (the X) only hides it, and the run keeps going. */}
            {!isTerminal && (
                <p className="mt-2 text-xs text-default-400">
                    You can close this. The report keeps building and will be in History.
                </p>
            )}

            {/* PPTX reports auto-navigate so no View button needed */}
            {isComplete && entry.lastReportId && entry.outputFormat !== 'pptx' && (
                <div className="mt-3 flex justify-end gap-2">
                    <Button size="sm" variant="flat" color="primary" onPress={openReport}>
                        View
                    </Button>
                </div>
            )}
        </motion.div>
    );
}

/**
 * Site-wide status pills for in-flight "Build a Report" runs.
 */
export function PlannerReportToasts() {
    const { reports, dismiss, cancel } = useActivePlannerReports();

    if (reports.length === 0) return null;

    return (
        <div className="pointer-events-none fixed bottom-6 right-6 z-[1000] flex flex-col gap-2">
            <AnimatePresence initial={false}>
                {reports.map((entry) => (
                    <ReportToastEntry
                        key={entry.runId}
                        entry={entry}
                        onDismiss={() => dismiss(entry.runId)}
                        onDone={() => cancel(entry.runId)}
                    />
                ))}
            </AnimatePresence>
        </div>
    );
}
